import { Button, MenuProps, Dropdown, Form, Image, Modal, Spin, Carousel } from 'antd';
import {
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useLayoutEffect,
} from 'react';
import dayjs from 'dayjs';
import { useNotificationContext } from '../../../contexts/NotificationContext';

import DataTable from '../../../components/common/DataTable';
import { usePermissions } from '../../../hooks/usePermissions';
import { PermissionEnum } from '../../../enums/PermissionEnum';
import {
  getAllCitizenReportedPriceDetails,
  updateCitizenPriceStatusBulk,
} from './getCitizenReportedData';
import { CheckOutlined, CloseOutlined, EyeOutlined, RedoOutlined } from '@ant-design/icons';
import { PriceStatusEnum } from '../../../enums/PriceStatusEnum';
import { PriceRecordSourceEnum } from '../../../enums/PriceRecordSourceEnum';
import { ThreeDotsVertical, XSquare } from 'react-bootstrap-icons';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import FormModelComponent from '../../../components/model/FormModelComponent';
import { useForm } from 'antd/es/form/Form';
import { FormTextArea } from '../../../components/form/FormBasic';
import DefaultButton from '../../../components/buttons/DefaultButton';
import './CitizenReportedPrices.scss';
import Tag from '../../../components/tag/Tag';
import { priceRecordType } from './CitizenReportedInspectionList';
import { PresentationStatusEnum } from '../../../enums/PresentationStatusEnum';
import { indexedDBManager } from '../../../utils/indexedDB';

export interface PriceReviewConfirmHandle {
  handlePriceReviewConfirm: () => void;
}

export interface pricesStatus {
  refId: string;
  status: string;
  remark?: string;
}

interface CitizenReportedPricesViewProps {
  t: (key: string, replace?: any) => string;
  selectedInspectionData: any;
  selectedInspectionPricesData: priceRecordType[];
  selectedInspectionId: any;
  enableDisableReviewConfirm: (hasStatusChanges: boolean) => void;
}

const CitizenReportedPricesView = forwardRef<
  PriceReviewConfirmHandle,
  CitizenReportedPricesViewProps
>((props, ref) => {
  const {
    t,
    selectedInspectionData,
    selectedInspectionPricesData,
    selectedInspectionId,
    enableDisableReviewConfirm,
  } = props;

  const [inspectionData, setInspectionData] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingCitizenInspections, setIsLoadingCitizenInspections] = useState<boolean>(false);
  const [pageSize] = useState<number>(8);
  const [totalInspections, setTotalInspections] = useState<number>(0);
  const [isViewModalVisible, setIsViewModalVisible] = useState<boolean>(false);
  const [selectedInspection, setSelectedInspection] = useState<any>(selectedInspectionData);

  const { successNotification, errorNotification } = useNotificationContext();

  const { hasPermission } = usePermissions();

  const [isApproveConfirmationOpen, setIsApproveConfirmationOpen] = useState<boolean>(false);
  const [isRevokeConfirmationOpen, setIsRevokeConfirmationOpen] = useState<boolean>(false);
  const [isPriceRejectModalOpen, setIsPriceRejectModalOpen] = useState<boolean>(false);

  const [selectedPriceRefId, setSelectedPriceRefId] = useState();
  const [priceRejectForm] = useForm();
  const [isStatusUpdating, setIsStatusUpdating] = useState<boolean>(false);
  const [priceStatuses, setPriceStatuses] = useState<any[]>([]);
  const [selectedInspectionPrices, setSelectedInspectionPrices] = useState<priceRecordType[]>([]);

  const [rowStatuses, setRowStatuses] = useState<Record<string, string>>({});
  const [selectedRowPhotos, setSelectedRowPhotos] = useState([]);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState<boolean>(false);
  const [selectedRowProduct, setSelectedRowProduct] = useState('');

  const priceTableRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState<undefined | number>(undefined);

  /* In price table show vertical scroll based on the available space */
  useLayoutEffect(() => {
    const currentContainer = priceTableRef?.current;

    if (!currentContainer) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const containerHeight = entry?.contentRect?.height;
        const offset = 130;
        const availableSpace = containerHeight - offset;

        const tableBody = currentContainer.querySelector(
          '.citizenPriceTable .ant-table-tbody'
        ) as HTMLElement | null;

        if (tableBody) {
          const actualContentHeight = tableBody.offsetHeight;

          if (actualContentHeight > availableSpace && availableSpace > 100) {
            setScrollY(availableSpace);
          } else {
            setScrollY(undefined);
          }
        }
      }
    });

    observer.observe(currentContainer);

    return () => {
      observer.disconnect();
    };
  }, [selectedInspectionPrices]);

  useEffect(() => {
    setSelectedInspectionPrices(selectedInspectionPricesData);
  }, [selectedInspectionPricesData]);

  /* Highlight the price record row based on the changed status */
  const updateRowStatuses = (refId: any, status: string) => {
    setRowStatuses((prev) => ({
      ...prev,
      [refId]: status,
    }));
  };

  const getRowClassName = (record: any) => {
    const status = rowStatuses[record?.refId];
    if (status === PriceStatusEnum.APPROVED) return 'row-approved';
    if (status === PriceStatusEnum.REJECTED) return 'row-rejected';
    if (status === PriceStatusEnum.REVOKED) return 'row-revoked';
    return '';
  };

  /* No button clicked in Approval confirmation Modal */
  const cancelApproveConfirmation = () => {
    setIsApproveConfirmationOpen(false);
  };

  /* Cancel button clicked in Revoke confirmation Modal */
  const cancelRevokeConfirmation = () => {
    setIsRevokeConfirmationOpen(false);
  };

  /* No button clicked in Reject confirmation Modal */
  const handleRejectCancel = () => {
    priceRejectForm.resetFields();
    setIsPriceRejectModalOpen(false);
  };

  /* Display Approval confirmation Modal */
  const handleApprovePrice = (refId: any) => {
    setSelectedPriceRefId(refId);
    setIsApproveConfirmationOpen(true);
  };

  /* Display Rejection confirmation Modal */
  const handleRejectPrice = (refId: any) => {
    setSelectedPriceRefId(refId);
    setIsPriceRejectModalOpen(true);
  };

  /* Display Revoke confirmation Modal */
  const handleRevokePrice = (refId: any) => {
    setSelectedPriceRefId(refId);
    setIsRevokeConfirmationOpen(true);
  };

  /* Individual Price record Approval confirmed */
  const handleApprovePriceConfirm = async () => {
    setIsStatusUpdating(true);

    setSelectedInspectionPrices((prevPriceData) =>
      prevPriceData?.map((priceRecord: any) =>
        priceRecord?.refId === selectedPriceRefId
          ? {
              ...priceRecord,
              status: PriceStatusEnum.APPROVED,
              isActionDisabled: true,
              rejectRemark: null,
            }
          : priceRecord
      )
    );

    setPriceStatuses((prevPriceStatus) => {
      const exists = prevPriceStatus?.some((price) => price?.refId === selectedPriceRefId);

      if (exists) {
        return prevPriceStatus?.map((price) =>
          price?.refId === selectedPriceRefId
            ? {
                ...price,
                status: PriceStatusEnum.APPROVED,
                rejectRemark: null,
              }
            : price
        );
      } else {
        return [
          ...prevPriceStatus,
          {
            refId: selectedPriceRefId,
            status: PriceStatusEnum.APPROVED,
            rejectRemark: null,
          },
        ];
      }
    });
    updateRowStatuses(selectedPriceRefId, PriceStatusEnum.APPROVED);
    setIsStatusUpdating(false);
    setIsApproveConfirmationOpen(false);
  };

  /* Confirm button show only when there is any status change */
  useEffect(() => {
    enableDisableReviewConfirm(priceStatuses?.length > 0 ? true : false);
  }, [priceStatuses]);

  /* Individual Price record Revoke confirmed */
  const handleRevokePriceConfirm = async () => {
    /* const body = {
      status: PriceStatusEnum.REVOKED,
    }; */

    setSelectedInspectionPrices((prevPriceData) =>
      prevPriceData?.map((priceRecord: any) =>
        priceRecord?.refId === selectedPriceRefId
          ? {
              ...priceRecord,
              status: PriceStatusEnum.REVOKED,
              isActionDisabled: true,
              rejectRemark: null,
            }
          : priceRecord
      )
    );

    setPriceStatuses((prevPriceStatus) => {
      const exists = prevPriceStatus?.some((price) => price?.refId === selectedPriceRefId);

      if (exists) {
        return prevPriceStatus?.map((price) =>
          price?.refId === selectedPriceRefId
            ? {
                ...price,
                status: PriceStatusEnum.REVOKED,
                rejectRemark: null,
              }
            : price
        );
      } else {
        return [
          ...prevPriceStatus,
          {
            refId: selectedPriceRefId,
            status: PriceStatusEnum.REVOKED,
            rejectRemark: null,
          },
        ];
      }
    });

    updateRowStatuses(selectedPriceRefId, PriceStatusEnum.REVOKED);
    setIsStatusUpdating(false);
    setIsRevokeConfirmationOpen(false);
  };

  /* Individual Price record Rejection confirmed */
  const handleRejectPriceConfirm = async () => {
    const rejectFormData = priceRejectForm.getFieldsValue();

    await priceRejectForm.validateFields();

    setSelectedInspectionPrices((prevPriceData) =>
      prevPriceData?.map((priceRecord: any) =>
        priceRecord?.refId === selectedPriceRefId
          ? {
              ...priceRecord,
              status: PriceStatusEnum.REJECTED,
              isActionDisabled: true,
              rejectRemark: rejectFormData?.rejectRemark,
            }
          : priceRecord
      )
    );

    setPriceStatuses((prevPriceStatus) => {
      const exists = prevPriceStatus?.some((price) => price?.refId === selectedPriceRefId);

      if (exists) {
        return prevPriceStatus?.map((price) =>
          price?.refId === selectedPriceRefId
            ? {
                ...price,
                status: PriceStatusEnum.REJECTED,
                rejectRemark: rejectFormData?.rejectRemark,
              }
            : price
        );
      } else {
        return [
          ...prevPriceStatus,
          {
            refId: selectedPriceRefId,
            status: PriceStatusEnum.REJECTED,
            rejectRemark: rejectFormData?.rejectRemark,
          },
        ];
      }
    });

    priceRejectForm.resetFields();
    updateRowStatuses(selectedPriceRefId, PriceStatusEnum.REJECTED);
    setIsStatusUpdating(false);
    setIsPriceRejectModalOpen(false);
  };

  useImperativeHandle(ref, () => ({
    async handlePriceReviewConfirm() {
      const body = {
        inspectionId: selectedInspectionId,
        prices: priceStatuses,
      };

      // setIsViewModalVisible(false);
      if (body?.prices?.length > 0) {
        setIsStatusUpdating(true);
        try {
          let result = await updateCitizenPriceStatusBulk(body);

          if (result) {
            const responseBody = result?.data;
            const dataPayload = responseBody?.data || responseBody;
            const successList = dataPayload?.success;

            if (Array.isArray(successList)) {
              for (const item of successList) {
                if (item.presentationRefId && item.presentationStatus) {
                  await indexedDBManager.updateLocalData('presentation', item?.presentationRefId, {
                    status: item?.presentationStatus,
                    updateTime: Date.now(),
                  });
                }
              }
            }

            successNotification(t('statusUpdateSuccess'));
          }
        } catch (error) {
          errorNotification(t('statusUpdateFail'));
        } finally {
          // setIsStatusUpdating(false);
          // setIsPriceRejectModalOpen(false);
        }
      } else {
        setIsStatusUpdating(false);
        setIsViewModalVisible(false);
      }
    },
  }));

  /* View Uploaded Images */
  const viewPhotos = (photos: any, product: string) => {
    setSelectedRowPhotos(photos);
    setSelectedRowProduct(product);
    setIsPhotoModalOpen(true);
  };

  /* Close Image View Popup */
  const handleClosePhotoModal = () => {
    setIsPhotoModalOpen(false);
    setSelectedRowPhotos([]);
  };

  /* Define Price table columns */
  const getPriceColumns = () => {
    let viewProductPricecolumns: any = [
      {
        key: 'presentation',
        title: t('presentation'),
        dataIndex: 'presentation',
        width: 100,

        render: (value: any, record: any) => (
          <span className="font-medium">{record?.product?.name || 'N/A'}</span>
        ),
      },
      {
        key: 'price',
        title: t('price'),
        dataIndex: 'price',
        width: 50,
        align: 'center' as const,
        render: (value: any, record: any) => (
          <span className="font-medium">{record?.price || 'N/A'}</span>
        ),
      },
      {
        key: 'source',
        title: t('source'),
        dataIndex: 'source',
        width: 60,
        align: 'center' as const,
        render: (value: any, record: any) => (
          <span className="font-medium">
            {record?.source ? t(record.source === PriceRecordSourceEnum.MOBILE ? 'mobile' : 'pc') : 'N/A'}
          </span>
        ),
      },
      {
        key: 'photoOrInvoice',
        title: t('photoOrInvoice'),
        dataIndex: 'photoOrInvoice',
        width: 80,
        align: 'center' as const,
        render: (value: any, record: any) => (
          <>
            <div>
              <DefaultButton
                handleOnClick={() => {
                  viewPhotos(record?.photoOrInvoice?.split(','), record?.product?.name);
                }}
              >
                <div className="flex flex-row items-center justify-center gap-2">
                  <EyeOutlined className="h-4 w-4 text-black" />
                  <p>{t('view')}</p>
                </div>
              </DefaultButton>
            </div>
          </>
        ),
      },
      {
        key: 'presentationType',
        title: t('presentationStatus'),
        dataIndex: 'presentationType',
        width: 50,
        align: 'center' as const,
        render: (value: any, record: any) => (
          <span className="font-medium">
            {record?.product?.status ? t(`common:status.${record?.product?.status}`) : 'N/A'}
          </span>
        ),
      },
      {
        key: 'status',
        title: t('status'),
        dataIndex: 'status',
        width: 80,
        align: 'center' as const,
        render: (value: any, record: any) => (
          <span
            className="flex justify-center font-medium"
            title={
              record?.status === PriceStatusEnum.REJECTED
                ? `${t('rejectRemark')} : ${record?.rejectRemark}`
                : undefined
            }
          >
            <Tag
              tagType={
                record?.status === PriceStatusEnum.APPROVED
                  ? 'success'
                  : record?.status === PriceStatusEnum.REVOKED
                    ? 'caution'
                    : record?.status === PriceStatusEnum.REJECTED
                      ? 'danger'
                      : 'neutral'
              }
              filled={false}
            >
              {t(`${record?.status?.toLowerCase()}`)}
            </Tag>
          </span>
        ),
      },
    ];

    hasPermission(PermissionEnum.REVIEW_PRICE_REQUESTS) &&
      viewProductPricecolumns.push({
        title: t('action'),
        dataIndex: 'action',
        key: 'action',
        width: 80,
        align: 'center' as const,
        render: (_: unknown, record: any) => {
          const items: MenuProps['items'] = [];

          if (record?.status === PriceStatusEnum.APPROVED) {
            /* items.push({
              key: 'revoke',
              label: t('revoke'),
              icon: <RedoOutlined className="text-blue-500" />,
              onClick: () => handleRevokePrice(record?.refId),
            }); */
          } else if (record?.status === PriceStatusEnum.REJECTED) {
            items.push({
              key: 'approve',
              label: t('approve'),
              icon: <CheckOutlined className="text-green-500" />,
              onClick: () => handleApprovePrice(record?.refId),
            });
          } else {
            items.push(
              {
                key: 'approve',
                label: t('approve'),
                icon: <CheckOutlined className="text-green-500" />,
                onClick: () => handleApprovePrice(record?.refId),
              },
              {
                key: 'reject',
                label: t('reject'),
                icon: <CloseOutlined className="text-red-500" />,
                onClick: () => handleRejectPrice(record?.refId),
              }
            );
          }

          return (
            <Dropdown
              menu={{ items, className: 'text-left' }}
              trigger={['click']}
              placement="bottomRight"
              getPopupContainer={() => document.body}
              disabled={record?.isActionDisabled || false}
            >
              <Button
                type="text"
                shape="circle"
                icon={
                  <ThreeDotsVertical
                    style={{ fontSize: '18px' }}
                    className={record?.isActionDisabled ? 'text-gray-300' : 'text-gray-600'}
                  />
                }
                disabled={record?.isActionDisabled || false}
                className="citizenPriceReviewAction"
              />
            </Dropdown>
          );
        },
      });
    return viewProductPricecolumns;
  };

  const handlePageChange = (page: number) => {
    getAllCitizenInspectionList(page);
  };

  const getAllCitizenInspectionList = useCallback(
    async (page: number = currentPage) => {
      setIsLoadingCitizenInspections(true);
      try {
        const paginationParams = {
          page,
          limit: pageSize,
        };

        const response = await getAllCitizenReportedPriceDetails(paginationParams);

        if (response.statusText === 'SUCCESS') {
          const inspections = response.data?.data || response.data || [];
          const total = response.data?.total || 0;
          setInspectionData(inspections);
          setTotalInspections(total);
          setCurrentPage(page);
        }
      } catch (error) {
        errorNotification(t('citizenReportedPriceFetchError'));
      } finally {
        setIsLoadingCitizenInspections(false);
      }
    },
    [pageSize, errorNotification, t]
  );

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-auto md:overflow-hidden">
        {/* Establishment Information */}
        <div className="flex-none overflow-y-auto p-4 md:max-h-[40%]">
          <div className="grid grid-cols-1 gap-y-3 px-2 md:grid-cols-2 md:gap-x-8 md:gap-y-4">
            <div className="flex flex-col md:flex-row md:items-baseline">
              <span className="w-full font-semibold text-gray-600 md:w-1/3">{t('department')}</span>
              <span className="text-gray-500 md:flex-1">
                {selectedInspection?.location?.district || 'N/A'}
              </span>
            </div>

            <div className="flex flex-col md:flex-row md:items-baseline">
              <span className="w-full font-semibold text-gray-600 md:w-1/3">
                {t('municipality')}
              </span>
              <span className="text-gray-500 md:flex-1">
                {selectedInspection?.location?.city || 'N/A'}
              </span>
            </div>

            <div className="flex flex-col md:flex-row md:items-baseline">
              <span className="w-full font-semibold text-gray-600 md:w-1/3">
                {t('establishmentType')}
              </span>
              <span className="text-gray-500 md:flex-1">
                {selectedInspection?.establishment_type || 'N/A'}
              </span>
            </div>

            <div className="flex flex-col md:flex-row md:items-baseline">
              <span className="w-full font-semibold text-gray-600 md:w-1/3">
                {t('establishment')}
              </span>
              <span className="text-gray-500 md:flex-1">
                {selectedInspection?.establishment?.name || 'N/A'}
              </span>
            </div>

            <div className="flex flex-col md:flex-row md:items-baseline">
              <span className="w-full font-semibold text-gray-600 md:w-1/3">
                {t('priceSubmitteDate')}
              </span>
              <span className="text-gray-500 md:flex-1">
                {dayjs(Number(selectedInspection?.reportedTime)).format('YYYY-MM-DD') || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Table with prices added for products */}
        <div className="flex min-h-0 flex-1 flex-col p-4">
          <div className="h-full w-full" ref={priceTableRef}>
            <DataTable
              data={selectedInspectionPrices ?? []}
              columns={getPriceColumns()}
              loading={isLoadingCitizenInspections}
              totalItems={selectedInspectionData?.prices.length}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              rowKey="id"
              showSizeChanger={false}
              showQuickJumper={true}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`
              }
              className="submitted-price-records-table price-table citizenPriceTable"
              scroll={{ x: 1000, y: scrollY }}
              rowClassName={getRowClassName}
              sticky={true}
            />
          </div>
        </div>
      </div>

      {/* Approve Popup for confirmation */}
      <ConfirmModelComponent
        isOpen={isApproveConfirmationOpen}
        onClickedCancel={cancelApproveConfirmation}
        header={t('approveTitle')}
        content={<p>{t('approveMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('yes'),
            onClick: handleApprovePriceConfirm,
            isDisabled: isStatusUpdating,
          },
          cancel: {
            label: t('no'),
            onClick: cancelApproveConfirmation,
          },
        }}
        styleClasses="!w-1/4 min-w-[360px] md:min-w-[380px]"
      />

      {/* Revoke Popup for confirmation */}
      <ConfirmModelComponent
        isOpen={isRevokeConfirmationOpen}
        onClickedCancel={cancelRevokeConfirmation}
        header={t('revokeTitle')}
        content={<p>{t('revokeMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('yes'),
            onClick: handleRevokePriceConfirm,
            isDisabled: isStatusUpdating,
          },
          cancel: {
            label: t('no'),
            onClick: cancelRevokeConfirmation,
          },
        }}
        styleClasses="!w-1/4 min-w-[360px] md:min-w-[380px]"
      />

      {/* Reject Popup to enter remarks */}
      <FormModelComponent
        isOpen={isPriceRejectModalOpen}
        header={t('rejectPriceTitle')}
        content={
          <>
            <p>{t('rejectMessage')}</p>
            <Form
              layout="vertical"
              form={priceRejectForm}
              name="priceRejectForm"
              // onFinish={taskCompletion}
              className="mt-2 w-full"
            >
              <FormTextArea
                name="rejectRemark"
                label={t('rejectRemarkLabel')}
                rules={[
                  { required: true, message: t('rejectRemarkRequired') },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(t('rejectRemarkSpaceValidation'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                placeholder={t('rejectRemarkPlaceholder')}
                disabled={false}
                isRequired={false}
              />
            </Form>
          </>
        }
        onClickedCancel={handleRejectCancel}
        footerAction={{
          confimation: {
            label: t('yes'),
            onClick: handleRejectPriceConfirm,
            isDisabled: isStatusUpdating,
          },
          cancel: {
            label: t('no'),
            onClick: handleRejectCancel,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[80vh] overflow-y-auto space-y-2"
      ></FormModelComponent>

      {/* View uploaded product photos */}
      <Modal
        title={t('photoModalTitle', { product: selectedRowProduct })}
        open={isPhotoModalOpen}
        onCancel={handleClosePhotoModal}
        footer={null}
        width="75%"
        centered
        destroyOnHidden
        className="w-[95vw] md:w-[70vw] lg:w-[50vw]"
        styles={{
          body: {
            padding: 0,
            // overflow: 'hidden'
          },
        }}
      >
        <div className="flex h-[60vh] flex-col justify-center px-4 md:h-[70vh]">
          <Carousel
            arrows
            infinite={false}
            // adaptiveHeight
            dotPosition="bottom"
            className="photo-carousel h-full"
          >
            {selectedRowPhotos?.map((image: any, index) => (
              <div
                key={index}
                className="flex h-[60vh] w-full items-center justify-center md:h-[70vh]"
              >
                <Image
                  src={image}
                  alt={t('photoOrInvoice')}
                  className="max-h-full max-w-full object-contain"
                  wrapperClassName="w-full h-full flex items-center justify-center"
                  loading="lazy"
                  placeholder={
                    <div className="flex h-full w-full items-center justify-center bg-gray-100">
                      <Spin />
                    </div>
                  }
                />
              </div>
            ))}
          </Carousel>
        </div>
      </Modal>
    </>
  );
});

export default CitizenReportedPricesView;
